import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search,
  Download,
  Play,
  Clock,
  Film,
  X,
  Calendar,
  AlertCircle,
  Loader2,
  LogOut,
  Video,
  Library,
  Hash,
  Car,
  Tag,
  MessageCircle,
  KeyRound,
  Mail,
  ArrowLeft,
  CheckCircle,
  Users,
  PlusCircle,
  Trash2,
  Edit,
  Send,
  Image as ImageIcon,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import posthog from 'posthog-js';

// --- CONFIGURAÇÕES GLOBAIS ---
const SUPABASE_URL = 'https://xxtxaxyvhchmbzkwvhpo.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh4dHhheHl2aGNobWJ6a3d2aHBvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUwMjAyODIsImV4cCI6MjA3MDU5NjI4Mn0.lUzUTNGUVv-Yi-QVsDqyeZdy_eXPPvapEAfiORlLmUE';
const CLOUDINARY_CLOUD_NAME = 'carboncars';
const BACKEND_URL_PROD = 'https://content.crbn.at';
const BACKEND_URL_DEV = 'https://content-dev.crbn.at';
const BACKEND_URL_LOCAL = 'http://localhost:5001';

// --- CONFIGURAÇÃO DO POSTHOG ---
// Use variáveis de ambiente para configurar o PostHog em tempo de build
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com'; // ou https://eu.i.posthog.com

if (typeof window !== 'undefined' && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false, // Pageviews são capturados manualmente no componente
    session_recording: {},
  });
  // Exponha para facilitar debug no console do navegador
  // @ts-ignore
  window.posthog = posthog;
  if (process.env.REACT_APP_POSTHOG_DEBUG === 'true') {
    posthog.debug(true);
  }
} else if (typeof window !== 'undefined') {
  // Evita silêncio total quando a chave não está definida
  console.warn('PostHog não inicializado: defina REACT_APP_POSTHOG_KEY no ambiente.');
}

const resolveBackendBaseUrl = (): string => {
  if (typeof window === 'undefined') {
    return BACKEND_URL_PROD;
  }

  const hostname = window.location.hostname.toLowerCase();

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return BACKEND_URL_LOCAL;
  }

  if (hostname.includes('content-dev.crbn.at')) {
    return BACKEND_URL_DEV;
  }

  return BACKEND_URL_PROD;
};

// --- INTERFACES TYPESCRIPT ---
interface User {
  id: string;
  email: string;
  user_metadata?: {
    name?: string;
    role?: string;
  };
}

interface AdminUser extends User {
    last_sign_in_at: string;
    created_at: string;
}

interface CloudinaryAsset {
  public_id: string;
  display_name: string;
  format: string;
  duration?: number;
  bytes: number;
  secure_url: string;
  created_at: string;
  tags: string[];
  resource_type: 'image' | 'video';
  context?: { alt?: string; custom?: { caption?: string; title?: string; }; [key: string]: any; };
  metadata?: { validade?: string; legenda?: string; montadora?: string; [key: string]: any; };
}

type SortableUserKeys = 'name' | 'email' | 'role' | 'last_sign_in_at';

// --- CLASSE DE API PARA O SUPABASE ---
class SupabaseClient {
  private url: string;
  private key: string;
  private token: string | null;
  private backendUrl: string;

  constructor() {
    this.url = SUPABASE_URL;
    this.key = SUPABASE_ANON_KEY;
    this.token = localStorage.getItem('supabase_token');
    this.backendUrl = resolveBackendBaseUrl();
  }

  private getHeaders(includeAuth: boolean = true): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'apikey': this.key, 'Authorization': `Bearer ${this.key}` };
    if (includeAuth && this.token) { headers['Authorization'] = `Bearer ${this.token}`; }
    return headers;
  }

  private async handleResponseError(response: Response): Promise<Error> {
    try {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Erro desconhecido';
        const errorDetails = errorData.details || 'Não há mais detalhes.';
        return new Error(`${errorMessage} - ${errorDetails}`);
    } catch (e) {
        return new Error(`Falha na requisição com status: ${response.status}`);
    }
  }

  async signIn(email: string, password: string): Promise<{ user: User, access_token: string }> {
    const response = await fetch(`${this.url}/auth/v1/token?grant_type=password`, { method: 'POST', headers: this.getHeaders(false), body: JSON.stringify({ email, password }) });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error_description || 'Erro de autenticação');
    this.token = data.access_token;
    localStorage.setItem('supabase_token', this.token!);
    localStorage.setItem('supabase_user', JSON.stringify(data.user));
    return { user: data.user, access_token: this.token! };
  }

  async signOut(): Promise<void> {
    await fetch(`${this.url}/auth/v1/logout`, { method: 'POST', headers: this.getHeaders(true) });
    this.token = null;
    localStorage.removeItem('supabase_token');
    localStorage.removeItem('supabase_user');
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    const response = await fetch(`${this.url}/auth/v1/recover`, { method: 'POST', headers: this.getHeaders(false), body: JSON.stringify({ email }) });
    if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.message || 'Falha ao enviar email.'); }
  }

  async updateUserPassword(accessToken: string, password: string): Promise<void> {
    const response = await fetch(`${this.url}/auth/v1/user`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'apikey': this.key, 'Authorization': `Bearer ${accessToken}` }, body: JSON.stringify({ password }) });
    if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.message || 'Falha ao atualizar a senha.'); }
  }

  getUser(): User | null {
    try {
      const user = localStorage.getItem('supabase_user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      localStorage.clear();
      return null;
    }
  }

  async adminGetUsers(): Promise<AdminUser[]> {
      const response = await fetch(`${this.backendUrl}/api/admin/users`, { headers: this.getHeaders() });
      if (!response.ok) throw await this.handleResponseError(response);
      const data = await response.json();
      return data.users;
  }

  async adminInviteUsers(users: { email: string, name: string }[]): Promise<{ successful: string[], failed: { email: string, reason: string }[] }> {
      const response = await fetch(`${this.backendUrl}/api/admin/invite`, { method: 'POST', headers: this.getHeaders(), body: JSON.stringify({ users }) });
      if (!response.ok) throw await this.handleResponseError(response);
      return await response.json();
  }

  async adminUpdateUser(userId: string, data: { role?: 'admin' | 'normal', name?: string }): Promise<any> {
      const response = await fetch(`${this.backendUrl}/api/admin/users/${userId}`, {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify(data)
      });
      if (!response.ok) throw await this.handleResponseError(response);
      return await response.json();
  }

  async adminDeleteUser(userId: string): Promise<void> {
      const response = await fetch(`${this.backendUrl}/api/admin/users/${userId}`, { method: 'DELETE', headers: this.getHeaders() });
      if (!response.ok) throw await this.handleResponseError(response);
  }

  async adminSendRecovery(email: string): Promise<any> {
      const response = await fetch(`${this.backendUrl}/api/admin/recover`, { method: 'POST', headers: this.getHeaders(), body: JSON.stringify({ email }) });
      if (!response.ok) throw await this.handleResponseError(response);
      return await response.json();
  }
}

// --- CLASSE DE API PARA O CLOUDINARY ---
class CloudinaryClient {
  private backendUrl: string;

  constructor() {
    this.backendUrl = resolveBackendBaseUrl();
  }

  async searchAssets(searchTerm: string): Promise<CloudinaryAsset[]> {
    const response = await fetch(`${this.backendUrl}/api/media/search`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ searchTerm }) });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro na busca');
    }
    const data = await response.json();
    return this.formatCloudinaryAssets(data.resources || []);
  }

  async getAllAssets(): Promise<CloudinaryAsset[]> {
    const response = await fetch(`${this.backendUrl}/api/media`, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao carregar mídias');
    }
    const data = await response.json();
    return this.formatCloudinaryAssets(data.resources || []);
  }

  private formatCloudinaryAssets(resources: any[]): CloudinaryAsset[] {
    return resources.map(resource => ({
      public_id: resource.public_id,
      display_name: resource.display_name || resource.public_id,
      format: resource.format || 'mp4',
      duration: resource.duration,
      bytes: resource.bytes || 0,
      secure_url: resource.secure_url,
      created_at: resource.created_at,
      tags: Array.isArray(resource.tags) ? resource.tags : [],
      resource_type: resource.resource_type,
      context: resource.context || {},
      metadata: resource.metadata || {}
    }));
  }
}

// --- COMPONENTES DE AUTENTICAÇÃO ---

type AuthView = 'login' | 'forgot_password' | 'update_password' | 'password_recovery_sent';

const AuthContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-black flex items-center justify-center p-5 relative">
    <img src="/bg_carbon.avif" alt="Fundo" className="absolute inset-0 w-full h-full object-cover z-0" style={{ pointerEvents: 'none', userSelect: 'none' }} />
    <div className="bg-white rounded-lg shadow-xl p-4 md:p-8 w-full max-w-md mx-5 z-20 relative">
      <div className="text-center mb-10 mt-5">
        <img src="/logo_carbon_content_b.png" alt="Logo Carbon Content" className="w-2/3 mx-auto mb-4" />
      </div>
      {children}
    </div>
  </div>
);

const ActionConfirmationView: React.FC = () => {
  const [message, setMessage] = useState('Redirecionando para a confirmação...');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const confirmationUrl = params.get('confirmation_url');
    if (confirmationUrl) {
      window.location.href = confirmationUrl;
    } else {
      setMessage('Link de confirmação inválido ou ausente.');
      setIsError(true);
    }
  }, []);

  return (
    <AuthContainer>
      <div className="text-center">
        {isError ? <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /> : <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />}
        <p className="text-lg text-gray-700">{message}</p>
      </div>
    </AuthContainer>
  );
};

const LoginView: React.FC<{ onLogin: (email: string, pass: string) => Promise<void>; isLoggingIn: boolean; authError: string; setAuthView: (view: AuthView) => void; }> = ({ onLogin, isLoggingIn, authError, setAuthView }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onLogin(email, password);
  };

  return (
    <AuthContainer>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 pl-10 border border-gray-300 rounded-lg" placeholder="seu-email@carbon.cars" required disabled={isLoggingIn} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <KeyRound className="h-5 w-5 text-gray-400" />
            </div>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 pl-10 border border-gray-300 rounded-lg" placeholder="Sua senha" required disabled={isLoggingIn} minLength={6} />
          </div>
        </div>
        {authError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{authError}</p>
              </div>
            </div>
          </div>
        )}
        <button type="submit" disabled={isLoggingIn} className="w-full bg-primary hover:bg-primary-hover transition-colors text-white py-3 px-4 rounded-lg font-medium disabled:opacity-50">
          {isLoggingIn ? <span className="flex items-center justify-center"><Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />Entrando...</span> : 'Acessar'}
        </button>
      </form>
      <div className="mt-6 text-center">
        <button onClick={() => setAuthView('forgot_password')} className="text-sm text-gray-600 hover:underline">
          Esqueceu sua senha?
        </button>
      </div>
    </AuthContainer>
  );
};

const ForgotPasswordView: React.FC<{ onRecover: (email: string) => Promise<void>; isLoggingIn: boolean; authError: string; setAuthView: (view: AuthView) => void; }> = ({ onRecover, isLoggingIn, authError, setAuthView }) => {
  const [email, setEmail] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRecover(email);
  };

  return (
    <AuthContainer>
      <h3 className="text-center text-xl font-semibold text-gray-800 mb-2">Recuperar Senha</h3>
      <p className="text-center text-gray-500 text-sm mb-6">Digite seu email para receber o link.</p>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 pl-10 border border-gray-300 rounded-lg" placeholder="seu-email@carbon.cars" required disabled={isLoggingIn} />
        </div>
        {authError && <p className="text-red-500 text-sm text-center">{authError}</p>}
        <button type="submit" disabled={isLoggingIn} className="w-full bg-primary hover:bg-primary-hover transition-colors text-white py-3 rounded-lg font-medium disabled:opacity-50">
          {isLoggingIn ? <Loader2 className="animate-spin mx-auto" /> : 'Enviar Link'}
        </button>
      </form>
      <button onClick={() => setAuthView('login')} className="mt-4 text-sm text-gray-600 hover:underline flex items-center justify-center w-full">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar para o Login
      </button>
    </AuthContainer>
  );
};

const PasswordRecoverySentView: React.FC<{ email: string; setAuthView: (view: AuthView) => void; }> = ({ email, setAuthView }) => (
  <AuthContainer>
    <div className="text-center">
      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-gray-800 mb-2">Verifique seu Email</h3>
      <p className="text-gray-600 mb-6">
        Se uma conta com o email <span className="font-bold">{email}</span> existir, enviamos um link para você redefinir sua senha.
      </p>
      <button onClick={() => setAuthView('login')} className="w-full bg-primary hover:bg-primary-hover transition-colors text-white py-3 rounded-lg font-medium">
        Voltar para o Login
      </button>
    </div>
  </AuthContainer>
);

const UpdatePasswordView: React.FC<{ onUpdate: (password: string) => Promise<void>; isLoggingIn: boolean; authError: string; }> = ({ onUpdate, isLoggingIn, authError }) => {
  const [password, setPassword] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(password);
  };

  return (
    <AuthContainer>
      <h3 className="text-center text-xl font-semibold text-gray-800 mb-2">Crie sua Nova Senha</h3>
      <p className="text-center text-gray-500 text-sm mb-6">Sua senha deve ter no mínimo 6 caracteres.</p>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <KeyRound className="h-5 w-5 text-gray-400" />
          </div>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 pl-10 border border-gray-300 rounded-lg" placeholder="Digite a nova senha" required disabled={isLoggingIn} minLength={6} />
        </div>
        {authError && <p className="text-red-500 text-sm text-center">{authError}</p>}
        <button type="submit" disabled={isLoggingIn} className="w-full bg-primary hover:bg-primary-hover transition-colors text-white py-3 rounded-lg font-medium disabled:opacity-50">
          {isLoggingIn ? <Loader2 className="animate-spin mx-auto" /> : 'Salvar Nova Senha'}
        </button>
      </form>
    </AuthContainer>
  );
};

// --- COMPONENTES DE MODAL ---
const InviteUserModal = ({ onInvite, onClose }: { onInvite: (users: { email: string, name: string }[]) => Promise<{ successful: string[], failed: any[] }>, onClose: () => void }) => {
    const [usersInput, setUsersInput] = useState('');
    const [isInviting, setIsInviting] = useState(false);
    const [results, setResults] = useState<{ successful: string[], failed: any[] } | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsInviting(true);
        setResults(null);

        const lines = usersInput.split('\n').filter(line => line.trim() !== '');
        const userList = lines.map(line => {
            const match = line.match(/(.*)<(.*)>/);
            if (match && match[1] && match[2]) {
                const name = match[1].trim();
                const email = match[2].trim();
                return { name, email };
            }
            const email = line.trim();
            if (email) {
              return { name: '', email };
            }
            return null;
        }).filter((user): user is { email: string; name: string } => user !== null);

        if (userList.length === 0) {
            alert("Por favor, insira pelo menos um usuário válido no formato 'Nome <email@exemplo.com>' ou apenas 'email@exemplo.com'.");
            setIsInviting(false);
            return;
        }
        try {
            const res = await onInvite(userList);
            setResults(res);
        } catch (e: any) {
            alert(`Erro ao processar a requisição: ${e.message}`);
        } finally {
            setIsInviting(false);
        }
    };

    const handleClose = () => {
        setResults(null);
        setUsersInput('');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">Convidar Novos Usuários</h3>
                <form onSubmit={handleSubmit}>
                    {!results ? (
                        <>
                            <label htmlFor="users-textarea" className="block text-sm font-medium text-gray-700 mb-2">
                                Usuários
                            </label>
                            <textarea
                                id="users-textarea"
                                value={usersInput}
                                onChange={e => setUsersInput(e.target.value)}
                                className="w-full p-2 border rounded mb-4 h-40"
                                placeholder="Use o formato: Nome <email@exemplo.com> (um por linha)"
                                required
                                disabled={isInviting}
                            />
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={handleClose} className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded transition-colors" disabled={isInviting}>Cancelar</button>
                                <button type="submit" className="bg-primary hover:bg-primary-hover transition-colors text-white px-4 py-2 rounded w-36 flex justify-center items-center" disabled={isInviting}>
                                    {isInviting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar Convites'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h4 className="font-bold mb-2 text-lg">Resultados do Envio</h4>
                            {results.successful.length > 0 && (
                                <div className="mb-4">
                                    <h5 className="text-green-600 font-semibold">{results.successful.length} Convites Enviados com Sucesso:</h5>
                                    <ul className="list-disc list-inside text-sm text-gray-600 max-h-24 overflow-y-auto bg-gray-50 p-2 rounded">
                                        {results.successful.map(email => <li key={email}>{email}</li>)}
                                    </ul>
                                </div>
                            )}
                            {results.failed.length > 0 && (
                                <div>
                                    <h5 className="text-red-600 font-semibold">{results.failed.length} Falhas:</h5>
                                    <ul className="list-disc list-inside text-sm text-gray-600 max-h-24 overflow-y-auto bg-gray-50 p-2 rounded">
                                        {results.failed.map(({ email, reason }) => <li key={email}><strong>{email}:</strong> {reason}</li>)}
                                    </ul>
                                </div>
                            )}
                            <div className="flex justify-end mt-4">
                                <button type="button" onClick={handleClose} className="bg-primary hover:bg-primary-hover transition-colors text-white px-4 py-2 rounded">Fechar</button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

const EditUserModal = ({ user, onUpdateUser, onClose }: { user: AdminUser, onUpdateUser: (userId: string, data: { role: 'admin' | 'normal', name: string }) => void, onClose: () => void }) => {
    const [role, setRole] = useState(user.user_metadata?.role || 'normal');
    const [name, setName] = useState(user.user_metadata?.name || '');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onUpdateUser(user.id, { role: role as 'admin' | 'normal', name });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-bold mb-2">Editar Usuário</h3>
                <p className="text-sm text-gray-500 mb-4 break-all">{user.email}</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="w-full p-2 border rounded"
                            placeholder="Nome completo do usuário"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
                        <select value={role} onChange={e => setRole(e.target.value)} className="w-full p-2 border rounded">
                            <option value="normal">Normal</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded transition-colors">Cancelar</button>
                        <button type="submit" className="bg-primary hover:bg-primary-hover transition-colors text-white px-4 py-2 rounded">Salvar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const DeleteUserModal = ({ user, onDelete, onClose }: { user: AdminUser, onDelete: (userId: string) => void, onClose: () => void }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">Confirmar Exclusão</h3>
            <p className="mb-4">Você tem certeza que deseja excluir o usuário <strong>{user.email}</strong>? Esta ação não pode ser desfeita.</p>
            <div className="flex justify-center gap-4">
                <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 transition-colors px-6 py-2 rounded">Cancelar</button>
                <button onClick={() => onDelete(user.id)} className="bg-red-700 hover:bg-red-800 transition-colors text-white px-6 py-2 rounded">Excluir</button>
            </div>
        </div>
    </div>
);

const RecoveryUserModal = ({ user, onSend, onClose }: { user: AdminUser, onSend: (email: string) => Promise<void>, onClose: () => void }) => {
    const [isSending, setIsSending] = useState(false);

    const handleSendClick = async () => {
        setIsSending(true);
        try {
            await onSend(user.email);
        } catch (e: any) {
            console.error(e);
            alert(`Erro ao enviar link: ${e.message}`);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
                <Send className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">Recuperação de Senha</h3>
                <p className="mb-4">Deseja enviar um link de recuperação de senha para <strong>{user.email}</strong>?</p>
                <div className="flex justify-center gap-4">
                    <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 transition-colors px-6 py-2 rounded" disabled={isSending}>Cancelar</button>
                    <button onClick={handleSendClick} className="bg-primary hover:bg-primary-hover transition-colors text-white px-6 py-2 rounded w-32 flex justify-center items-center" disabled={isSending}>
                        {isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE DE GERENCIAMENTO DE USUÁRIOS ---
const UserManagement = () => {
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState<AdminUser | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<AdminUser | null>(null);
    const [showRecoveryModal, setShowRecoveryModal] = useState<AdminUser | null>(null);
    const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'normal'>('all');
    const [sortConfig, setSortConfig] = useState<{ key: SortableUserKeys, direction: 'asc' | 'desc' }>({ key: 'last_sign_in_at', direction: 'desc' });
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const supabase = useRef(new SupabaseClient());

    const fetchUsers = async () => {
        setIsLoading(true);
        setError('');
        try {
            const userList = await supabase.current.adminGetUsers();
            setUsers(userList);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleInvite = async (usersToInvite: { email: string, name: string }[]): Promise<{ successful: string[], failed: any[] }> => {
        const results = await supabase.current.adminInviteUsers(usersToInvite);
        if (results.successful.length > 0) {
            posthog?.capture('admin_users_invited', { count: results.successful.length });
            fetchUsers();
        }
        if (results.failed.length > 0) {
            posthog?.capture('admin_users_invite_failed', { count: results.failed.length });
        }
        return results;
    };

    const handleUpdateUser = async (userId: string, data: { role: 'admin' | 'normal', name: string }) => {
        try {
            await supabase.current.adminUpdateUser(userId, data);
            posthog?.capture('admin_user_updated', { user_id: userId, updated_role: data.role });
            setShowEditModal(null);
            fetchUsers();
        } catch (e: any) {
            alert(`Erro ao atualizar usuário: ${e.message}`);
        }
    };
    
    const handleDelete = async (userId: string) => {
        try {
            await supabase.current.adminDeleteUser(userId);
            posthog?.capture('admin_user_deleted', { user_id: userId });
            setShowDeleteModal(null);
            fetchUsers();
        } catch (e: any) {
            alert(`Erro ao deletar usuário: ${e.message}`);
        }
    };

    const handleSendRecovery = async (email: string) => {
        await supabase.current.adminSendRecovery(email);
        posthog?.capture('admin_user_recovery_sent', { email: email });
        setShowRecoveryModal(null);
        alert('Link de recuperação de senha enviado com sucesso!');
    };

    const handleSort = (key: SortableUserKeys) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedUsers = useMemo(() => {
        let sortableUsers = [...users];

        // Search
        if (userSearchTerm) {
            const lowercasedTerm = userSearchTerm.toLowerCase();
            sortableUsers = sortableUsers.filter(user =>
                (user.user_metadata?.name?.toLowerCase() || '').includes(lowercasedTerm) ||
                (user.email?.toLowerCase() || '').includes(lowercasedTerm)
            );
        }

        // Filtering by role
        sortableUsers = sortableUsers.filter(user => {
            if (roleFilter === 'all') return true;
            const role = user.user_metadata?.role || 'normal';
            return role === roleFilter;
        });

        // Sorting
        sortableUsers.sort((a, b) => {
            let aValue: any = '';
            let bValue: any = '';

            switch (sortConfig.key) {
                case 'name':
                    aValue = a.user_metadata?.name?.toLowerCase() || '';
                    bValue = b.user_metadata?.name?.toLowerCase() || '';
                    break;
                case 'email':
                    aValue = a.email?.toLowerCase() || '';
                    bValue = b.email?.toLowerCase() || '';
                    break;
                case 'role':
                    aValue = a.user_metadata?.role?.toLowerCase() || 'normal';
                    bValue = b.user_metadata?.role?.toLowerCase() || 'normal';
                    break;
                case 'last_sign_in_at':
                    aValue = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0;
                    bValue = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0;
                    break;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sortableUsers;
    }, [users, roleFilter, sortConfig, userSearchTerm]);
    
    const SortableHeader: React.FC<{ columnKey: SortableUserKeys, title: string }> = ({ columnKey, title }) => (
        <th className="text-left py-3 px-4 font-semibold text-sm">
            <button onClick={() => handleSort(columnKey)} className="flex items-center gap-1 hover:text-gray-900">
                {title}
                {sortConfig.key === columnKey && (
                    sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                )}
            </button>
        </th>
    );

    return (
        <div className="bg-white md:rounded-lg md:shadow-md p-6 mt-10">
            <div className="mb-6">
                <h2 className="text-xl font-bold flex items-center mb-4">
                    <Users className="w-5 h-5 mr-2" />
                    Gerenciamento de Usuários
                    <span className="ml-2 text-sm bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md">
                        {users.length}
                    </span>
                </h2>
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="relative w-full md:max-w-xs">
                        <input
                            type="text"
                            placeholder="Buscar por nome ou email..."
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                            className="w-full p-2 pl-10 border border-gray-300 rounded-md"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                        <div className="w-full sm:w-48">
                            <select
                                id="roleFilter"
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value as 'all' | 'admin' | 'normal')}
                                className="w-full p-2 border border-gray-300 rounded-md"
                            >
                                <option value="all">Todos os Perfis</option>
                                <option value="admin">Admin</option>
                                <option value="normal">Normal</option>
                            </select>
                        </div>
                        <button
                            onClick={() => setShowInviteModal(true)}
                            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-hover flex items-center gap-2 transition-all w-full sm:w-auto justify-center"
                        >
                            <PlusCircle className="w-5 h-5" />
                            Convidar
                        </button>
                    </div>
                </div>
            </div>

            {isLoading && (
                <div className="text-center py-12">
                    <Loader2 className="animate-spin h-12 w-12 text-primary mx-auto" />
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                    <h3 className="font-bold text-red-800">Ocorreu um erro</h3>
                    <p className="text-red-700 text-sm">{error}</p>
                </div>
            )}
            
            {!isLoading && !error && (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead className="bg-gray-100">
                            <tr>
                                <SortableHeader columnKey="name" title="Nome" />
                                <SortableHeader columnKey="email" title="Email" />
                                <SortableHeader columnKey="role" title="Perfil" />
                                <SortableHeader columnKey="last_sign_in_at" title="Último Login" />
                                <th className="text-left py-3 px-4 font-semibold text-sm">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedUsers.map(user => {
                                const role = user.user_metadata?.role || 'normal';
                                return (
                                    <tr key={user.id} className="border-b hover:bg-gray-50">
                                        <td className="py-3 px-4">{user.user_metadata?.name || <span className="text-gray-400">Não definido</span>}</td>
                                        <td className="py-3 px-4">{user.email}</td>
                                        <td className="py-3 px-4">
                                            <span className={`px-2 py-1 text-xs rounded-full ${role === 'admin' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-800'}`}>
                                                {role === 'admin' ? 'Admin' : 'Normal'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">{user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString('pt-BR') : 'Nunca'}</td>
                                        <td className="py-3 px-4 flex gap-3">
                                            <div className="relative group">
                                                <button onClick={() => setShowRecoveryModal(user)} className="text-gray-600 hover:text-gray-800"><Send className="w-5 h-5" /></button>
                                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-700 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Enviar Recuperação</span>
                                            </div>
                                            <div className="relative group">
                                                <button onClick={() => setShowEditModal(user)} className="text-gray-600 hover:text-gray-800"><Edit className="w-5 h-5" /></button>
                                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-700 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Editar Usuário</span>
                                            </div>
                                            <div className="relative group">
                                                <button onClick={() => setShowDeleteModal(user)} className="text-red-600 hover:text-red-800"><Trash2 className="w-5 h-5" /></button>
                                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-700 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Excluir Usuário</span>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {showInviteModal && <InviteUserModal onInvite={handleInvite} onClose={() => setShowInviteModal(false)} />}
            {showEditModal && <EditUserModal user={showEditModal} onUpdateUser={handleUpdateUser} onClose={() => setShowEditModal(null)} />}
            {showDeleteModal && <DeleteUserModal user={showDeleteModal} onDelete={handleDelete} onClose={() => setShowDeleteModal(null)} />}
            {showRecoveryModal && <RecoveryUserModal user={showRecoveryModal} onSend={handleSendRecovery} onClose={() => setShowRecoveryModal(null)} />}
        </div>
    );
};


// --- COMPONENTES DA APLICAÇÃO PRINCIPAL ---

const PostHogPageViewTracker = () => {
    useEffect(() => {
        if (posthog) {
            posthog.capture('$pageview');
        }
    }, []);
    return null;
};


const AssetThumbnail: React.FC<{ asset: CloudinaryAsset; onClick: () => void }> = ({ asset, onClick }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const handleThumbnailClick = () => {
    posthog?.capture('media_asset_viewed', {
        asset_id: asset.public_id,
        asset_type: asset.resource_type,
        display_name: asset.display_name,
    });
    onClick();
  };

  const getThumbnailUrl = (width: number = 400, height: number = 225) => {
    if (asset.resource_type === 'video') {
      return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/video/upload/w_${width},h_${height},c_fill,q_auto,f_auto,so_0/${asset.public_id}.jpg`;
    }
    return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/w_${width},h_${height},c_fill,q_auto,f_auto/${asset.public_id}.${asset.format}`;
  };

  const thumbnailUrl = getThumbnailUrl(400, 225);
  const fallbackUrl = asset.resource_type === 'video' ? getThumbnailUrl(400, 225).replace('so_0', 'so_1') : thumbnailUrl;
  const lowQualityUrl = getThumbnailUrl(200, 113);

  const handleImageLoad = () => setImageLoading(false);
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    if (!imageError) {
      setImageError(true);
      target.src = fallbackUrl;
    } else {
      setImageLoading(false);
    }
  };

  return (
    <div className="relative cursor-pointer group" onClick={handleThumbnailClick}>
      {imageLoading && (
        <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center animate-pulse">
          {asset.resource_type === 'video' ? <Film className="w-8 h-8 text-gray-400" /> : <ImageIcon className="w-8 h-8 text-gray-400" />}
        </div>
      )}
      <img src={lowQualityUrl} alt="" className={`absolute inset-0 w-full h-48 object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-100' : 'opacity-0'}`} loading="lazy" />
      <img src={thumbnailUrl} alt={asset.context?.custom?.title || asset.display_name} className={`w-full h-48 object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`} loading="lazy" onLoad={handleImageLoad} onError={handleImageError} />
      
      {!imageLoading && imageError && (
        <div className="absolute inset-0 w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
            {asset.resource_type === 'video' ? <Video className="w-8 h-8 text-blue-600" /> : <ImageIcon className="w-8 h-8 text-blue-600" />}
        </div>
      )}

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white bg-opacity-25 backdrop-blur-sm rounded-xl py-3 px-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg">
          {asset.resource_type === 'video' ? <Play className="text-white w-8 h-8 fill-current transform translate-x-0.5" /> : <Search className="text-white w-8 h-8" />}
        </div>
      </div>
      
    </div>
  );
};

const MainApp = () => {
  const [user, setUser] = useState<User | null>(null);
  const [assets, setAssets] = useState<CloudinaryAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<CloudinaryAsset | null>(null);
  const [downloadingAssets, setDownloadingAssets] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMontadora, setSelectedMontadora] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [appliedSelectedMontadora, setAppliedSelectedMontadora] = useState('');
  const [appliedSelectedTag, setAppliedSelectedTag] = useState('');
  const [availableMontadoras, setAvailableMontadoras] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const supabase = useRef(new SupabaseClient());
  const cloudinary = useRef(new CloudinaryClient());
  const [authView, setAuthView] = useState<AuthView>('login');
  const [authError, setAuthError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [recoveryToken, setRecoveryToken] = useState<string | null>(null);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [currentView, setCurrentView] = useState<'library' | 'users'>('library');
  const [shareNotification, setShareNotification] = useState<string | null>(null);

  useEffect(() => {
    const storedUser = supabase.current.getUser();
    if (storedUser) {
      setUser(storedUser);
    }
    const handleAuthRedirect = () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      const errorDescription = hashParams.get('error_description');
      if (accessToken && (type === 'recovery' || type === 'invite')) {
        setRecoveryToken(accessToken);
        setAuthView('update_password');
        window.history.replaceState(null, '', window.location.pathname);
      } else if (errorDescription) {
        const friendlyError = errorDescription.replace(/\+/g, ' ');
        setAuthError(`Erro: ${friendlyError}.`);
        window.history.replaceState(null, '', window.location.pathname);
      }
    };
    handleAuthRedirect();
    window.addEventListener('hashchange', handleAuthRedirect);
    return () => {
      window.removeEventListener('hashchange', handleAuthRedirect);
    };
  }, []);

  useEffect(() => {
    if (user && posthog) {
        posthog.identify(user.id, {
            email: user.email,
            name: user.user_metadata?.name,
        });
        loadAllAssets();
    } else if (!user && posthog) {
        posthog.reset();
    }
  }, [user]);

  useEffect(() => {
    setAvailableMontadoras(extractMontadoras(assets));
    setAvailableTags(extractTags(assets));
  }, [assets]);

  useEffect(() => {
    if (user) executeSearch();
  }, [user, appliedSearchTerm, appliedSelectedMontadora, appliedSelectedTag]);

  const normalizeText = (value?: string | null): string => (value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const extractMontadoras = (assets: CloudinaryAsset[]): string[] => { const montadoras = new Set<string>(); assets.forEach(asset => { const montadora = asset.metadata?.montadora; if (montadora && montadora.trim()) montadoras.add(montadora.trim()); }); return Array.from(montadoras).sort(); };
  const extractTags = (assets: CloudinaryAsset[]): string[] => { const tags = new Set<string>(); assets.forEach(asset => { if (Array.isArray(asset.tags)) { asset.tags.forEach(tag => { if (tag && tag.trim()) tags.add(tag.trim()); }); } }); return Array.from(tags).sort(); };
  const filterAssetsByMontadora = (assets: CloudinaryAsset[], montadora: string): CloudinaryAsset[] => { const normalizedFilter = normalizeText(montadora); if (!normalizedFilter) return assets; return assets.filter(asset => normalizeText(asset.metadata?.montadora).includes(normalizedFilter)); };
  const filterAssetsByTag = (assets: CloudinaryAsset[], tag: string): CloudinaryAsset[] => { const normalizedTag = normalizeText(tag); if (!normalizedTag) return assets; return assets.filter(asset => Array.isArray(asset.tags) && asset.tags.some(assetTag => normalizeText(assetTag).includes(normalizedTag))); };
  const filterAssetsBySearchTerm = (assets: CloudinaryAsset[], searchTerm: string): CloudinaryAsset[] => { const term = normalizeText(searchTerm); if (!term) return assets; return assets.filter(asset => { if ((asset.context?.custom?.title || asset.display_name) && normalizeText(asset.context?.custom?.title || asset.display_name).includes(term)) return true; if ((asset.metadata?.legenda || asset.context?.custom?.caption) && normalizeText(asset.metadata?.legenda || asset.context?.custom?.caption).includes(term)) return true; if (Array.isArray(asset.tags) && asset.tags.some(tag => normalizeText(tag).includes(term))) return true; if (normalizeText(asset.metadata?.montadora).includes(term)) return true; return false; }); };
  const getFilteredAssets = (): CloudinaryAsset[] => { let filtered = assets; filtered = filterAssetsBySearchTerm(filtered, appliedSearchTerm); filtered = filterAssetsByMontadora(filtered, appliedSelectedMontadora); filtered = filterAssetsByTag(filtered, appliedSelectedTag); return filtered; };
  const filteredAssets = getFilteredAssets();
  const loadAllAssets = async () => { if (!user) return; setLoading(true); try { const cloudinaryAssets = await cloudinary.current.getAllAssets(); setAssets(cloudinaryAssets); } catch (error: any) { console.error("Erro ao carregar biblioteca:", error); setAssets([]); } finally { setLoading(false); } };
  const executeSearch = async () => { if (!user) return; setLoading(true); try { if (!appliedSearchTerm.trim() && !appliedSelectedMontadora && !appliedSelectedTag) { await loadAllAssets(); return; } const cloudinaryAssets = await cloudinary.current.searchAssets(appliedSearchTerm); let filteredResults = filterAssetsByMontadora(cloudinaryAssets, appliedSelectedMontadora); filteredResults = filterAssetsByTag(filteredResults, appliedSelectedTag); setAssets(filteredResults); } catch (error: any) { console.error("Erro na busca:", error); setAssets([]); } finally { setLoading(false); } };
  const handleSearchButtonClick = () => {
      posthog?.capture('media_searched', {
          search_term: searchTerm,
          filters: {
              montadora: appliedSelectedMontadora,
              tag: appliedSelectedTag,
          }
      });
      setAppliedSearchTerm(searchTerm);
  };
  const handleSearchInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') handleSearchButtonClick(); };
  const clearAllFilters = () => {
      posthog?.capture('media_filters_cleared');
      setSearchTerm(''); setSelectedMontadora(''); setSelectedTag(''); setAppliedSearchTerm(''); setAppliedSelectedMontadora(''); setAppliedSelectedTag('');
  };
  const handleLogin = async (email: string, pass: string) => {
      setAuthError('');
      setIsLoggingIn(true);
      try {
          const { user } = await supabase.current.signIn(email, pass);
          posthog?.capture('user_login_success');
          setUser(user);
      } catch (error: any) {
          let errorMessage = 'Erro de autenticação';
          if (error && error.message) {
              const msg = error.message.toLowerCase();
              if (msg.includes('invalid login credentials')) errorMessage = 'Email ou senha incorretos.';
              else if (msg.includes('user not found')) errorMessage = 'Usuário não encontrado.';
              else if (msg.includes('rate limit')) errorMessage = 'Muitas tentativas.';
              else errorMessage = error.message;
          }
          posthog?.capture('user_login_failed', { error: errorMessage });
          setAuthError(errorMessage);
      } finally {
          setIsLoggingIn(false);
      }
  };
  const handleLogout = async () => {
      posthog?.capture('user_logout');
      await supabase.current.signOut();
      setUser(null);
      setAssets([]);
      window.history.replaceState(null, '', window.location.pathname);
  };
  const handlePasswordRecovery = async (email: string) => {
      setAuthError('');
      setIsLoggingIn(true);
      try {
          await supabase.current.sendPasswordResetEmail(email);
          posthog?.capture('user_password_recovery_requested', { email });
          setRecoveryEmail(email);
          setAuthView('password_recovery_sent');
      } catch (error: any) {
          setAuthError(error.message || 'Não foi possível enviar o email.');
      } finally {
          setIsLoggingIn(false);
      }
  };
  const handleUpdatePassword = async (password: string) => {
      if (!recoveryToken) { setAuthError("Token inválido."); return; }
      if (password.length < 6) { setAuthError("A senha deve ter no mínimo 6 caracteres."); return; }
      setAuthError('');
      setIsLoggingIn(true);
      try {
          await supabase.current.updateUserPassword(recoveryToken, password);
          posthog?.capture('user_password_updated');
          alert("Senha definida com sucesso! Agora você pode fazer o login.");
          setAuthView('login');
          window.history.replaceState(null, '', window.location.pathname);
      } catch (error: any) {
          setAuthError(error.message || 'Não foi possível atualizar a senha.');
      } finally {
          setIsLoggingIn(false);
      }
  };
  const handleDownload = async (asset: CloudinaryAsset) => {
      setDownloadingAssets(prev => new Set(prev).add(asset.public_id));
      posthog?.capture('media_asset_downloaded', {
          asset_id: asset.public_id,
          asset_type: asset.resource_type,
          file_name: `${asset.context?.custom?.title || asset.display_name || asset.public_id}.${asset.format}`
      });
      try {
          const cleanTitle = (asset.context?.custom?.title || asset.display_name || asset.public_id).replace(/[^a-zA-Z0-9\s\-_]/g, '');
          const fileName = `${cleanTitle}.${asset.format}`;
          const response = await fetch(asset.secure_url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
          console.error('Erro no download:', error);
          window.open(asset.secure_url, '_blank');
      } finally {
          setTimeout(() => {
              setDownloadingAssets(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(asset.public_id);
                  return newSet;
              });
          }, 2000);
      }
  };
  const shareAssetViaWebShare = async (asset: CloudinaryAsset) => {
      posthog?.capture('media_asset_shared', {
          asset_id: asset.public_id,
          asset_type: asset.resource_type,
          display_name: asset.display_name || asset.context?.custom?.title || asset.public_id,
          share_method: 'web_share_api'
      });

      const getMimeType = (resType: 'image' | 'video', fmt: string) => {
          const format = (fmt || '').toLowerCase();
          if (resType === 'video') return `video/${format}`;
          switch (format) {
              case 'jpg': case 'jpeg': case 'jfif': return 'image/jpeg';
              case 'svg': return 'image/svg+xml';
              case 'ico': return 'image/x-icon';
              default: return `image/${format}`;
          }
      };

      const getCaptionText = (assetToShare: CloudinaryAsset) => {
          const possibleTexts = [
              assetToShare.context?.alt,
              assetToShare.metadata?.legenda,
              assetToShare.context?.custom?.caption
          ];
          for (const value of possibleTexts) {
              if (typeof value === 'string') {
                  const trimmed = value.trim();
                  if (trimmed.length > 0) return trimmed;
              }
          }
          return '';
      };

      try {
          if (!navigator.share) {
              alert('O compartilhamento não é suportado neste navegador.');
              return;
          }

          const response = await fetch(asset.secure_url);
          if (!response.ok) throw new Error('Erro ao baixar mídia para compartilhamento');
          const blob = await response.blob();
          const fileName = `${asset.display_name}.${asset.format}`;
          const mimeType = getMimeType(asset.resource_type, asset.format);
          const file = new File([blob], fileName, { type: mimeType });
          
          const captionText = getCaptionText(asset);
          const title = asset.context?.custom?.title || asset.display_name || fileName;

          const fileShareData: ShareData = { files: [file], title: title, text: captionText };
          const linkShareData: ShareData = { url: asset.secure_url, title: title, text: captionText };
          
          const isWindows = typeof navigator !== 'undefined' && /windows/i.test(navigator.userAgent || '');

          if (navigator.canShare && navigator.canShare(fileShareData)) {
              if (isWindows && captionText && navigator.clipboard?.writeText) {
                  try {
                      await navigator.clipboard.writeText(captionText);
                      setShareNotification('Legenda copiada! Cole na mensagem se precisar.');
                      setTimeout(() => setShareNotification(null), 4000);
                  } catch (clipboardError) {
                      console.warn('Falha ao copiar a legenda para a área de transferência:', clipboardError);
                  }
              }
              await navigator.share(fileShareData);
          } 
          else if (navigator.canShare && navigator.canShare(linkShareData)) {
              await navigator.share(linkShareData);
          } 
          else {
              alert('Não é possível compartilhar este tipo de conteúdo neste navegador.');
          }
      } catch (err: any) {
          const msg = String(err?.message || '').toLowerCase();
          const name = String(err?.name || '');
          if (name === 'AbortError' || msg.includes('share canceled') || msg.includes('share cancelled')) {
              return;
          }
          console.error('Erro no compartilhamento:', err);
      }
  };
  
  const isAdmin = user?.user_metadata?.role === 'admin';

  if (!user) {
    switch (authView) {
      case 'forgot_password':
        return <ForgotPasswordView onRecover={handlePasswordRecovery} isLoggingIn={isLoggingIn} authError={authError} setAuthView={setAuthView} />;
      case 'password_recovery_sent':
        return <PasswordRecoverySentView email={recoveryEmail} setAuthView={setAuthView} />;
      case 'update_password':
        return <UpdatePasswordView onUpdate={handleUpdatePassword} isLoggingIn={isLoggingIn} authError={authError} />;
      default:
        return <LoginView onLogin={handleLogin} isLoggingIn={isLoggingIn} authError={authError} setAuthView={setAuthView} />;
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PostHogPageViewTracker />
      <header className="bg-black text-white shadow-lg sticky top-0 relative z-20">
        <img src="/bg_carbon.avif" alt="Fundo" className="absolute inset-0 w-full h-full md:object-center object-top object-cover z-0" />
        <div className="max-w-6xl mx-auto py-4 px-6 flex justify-between items-center z-20 relative">
          <img src="/logo_carbon_content_h_white.png" alt="CARBON Content" className="md:h-6 h-4" />
          <div className="flex items-center gap-2 md:gap-4">
              {isAdmin && currentView === 'library' && (
                  <button onClick={() => { posthog?.capture('admin_viewed_user_management'); setCurrentView('users'); }} className="bg-gray-700 px-4 py-3 md:py-2 rounded hover:bg-gray-800 transition-colors flex items-center gap-2"><Users className="w-4 h-4" /><span className="hidden md:flex">Gerenciar</span></button>
              )}
              {isAdmin && currentView === 'users' && (
                  <button onClick={() => setCurrentView('library')} className="bg-gray-700 px-4 py-3 md:py-2 rounded hover:bg-gray-800 transition-colors flex items-center gap-2"><Library className="w-4 h-4" /><span className="hidden md:flex">Biblioteca</span></button>
              )}
            <button onClick={handleLogout} className="bg-red-700 px-4 py-3 md:py-2 rounded hover:bg-red-800 transition-colors flex items-center gap-2"><LogOut className="w-4 h-4" /><span className="hidden md:flex">Sair</span></button>
          </div>
        </div>
      </header>
      
      <main className="flex-grow relative z-0">
        <div className="max-w-6xl mx-auto md:p-6">
            {currentView === 'library' ? (
                <>
                    {isAdmin && <div className="bg-green-100 border border-green-200 text-green-800 p-4 rounded-lg m-6 md:m-0 md:mb-6 flex items-center"><AlertCircle className="w-5 h-5 mr-3" />Você está logado como Administrador.</div>}
                    <div className="bg-white md:rounded-lg md:shadow-md p-6 md:mb-6">
                        <h2 className="text-xl font-bold mb-4 flex items-center"><Search className="w-5 h-5 mr-2" />Buscar Mídia</h2>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Buscar por palavras-chave</label>
                                    <div className="relative">
                                        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={handleSearchInputKeyPress} className="w-full p-3 pr-10 border border-gray-300 rounded-md" placeholder="Digite palavras-chave..." />
                                        {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Filtrar por montadora</label>
                                    <div className="relative">
                                        <select value={selectedMontadora} onChange={(e) => { setSelectedMontadora(e.target.value); setAppliedSelectedMontadora(e.target.value); posthog?.capture('media_filter_changed', { filter: 'montadora', value: e.target.value }); }} className="w-full p-3 pr-10 border border-gray-300 rounded-md appearance-none bg-white">
                                            <option value="">Todas as montadoras</option>
                                            {availableMontadoras.map((montadora) => <option key={montadora} value={montadora}>{montadora.toUpperCase()}</option>)}
                                        </select>
                                        {selectedMontadora && <button onClick={() => { setSelectedMontadora(''); setAppliedSelectedMontadora(''); }} className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"><Car className="w-4 h-4 text-gray-400" /></div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Filtrar por tag</label>
                                    <div className="relative">
                                        <select value={selectedTag} onChange={(e) => { setSelectedTag(e.target.value); setAppliedSelectedTag(e.target.value); posthog?.capture('media_filter_changed', { filter: 'tag', value: e.target.value }); }} className="w-full p-3 pr-10 border border-gray-300 rounded-md appearance-none bg-white">
                                            <option value="">Todas as tags</option>
                                            {availableTags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
                                        </select>
                                        {selectedTag && <button onClick={() => { setSelectedTag(''); setAppliedSelectedTag(''); }} className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"><Tag className="w-4 h-4 text-gray-400" /></div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t">
                                <div className="flex items-center gap-4">
                                    <button onClick={handleSearchButtonClick} className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-hover flex items-center gap-2 transition-colors" disabled={loading}><Search className="w-4 h-4" />Buscar</button>
                                    {(appliedSearchTerm || appliedSelectedMontadora || appliedSelectedTag) && <button onClick={clearAllFilters} className="bg-red-100 text-red-600 px-4 py-2 rounded-md hover:bg-red-200 flex items-center gap-2"><X className="w-4 h-4" />Limpar Filtros</button>}
                                </div>
                                <div className="text-sm text-gray-600">
                                    {appliedSearchTerm && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-2">Busca: "{appliedSearchTerm}"</span>}
                                    {appliedSelectedMontadora && <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full mr-2">Montadora: {appliedSelectedMontadora.toUpperCase()}</span>}
                                    {appliedSelectedTag && <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full mr-2">Tag: {appliedSelectedTag}</span>}
                                    {!appliedSearchTerm && !appliedSelectedMontadora && !appliedSelectedTag && <span className="text-gray-500">Nenhum filtro ativo</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white md:rounded-lg md:shadow-md p-6 mt-10">
                        <div className="mb-4">
                            <div className="flex items-center mb-2 md:mb-0"><Library className="w-5 h-5 mr-2" /><h2 className="text-xl font-bold">Biblioteca de Mídia</h2></div>
                            <div className="text-base font-normal text-gray-600 md:ml-7">{filteredAssets.length} {filteredAssets.length === 1 ? 'item' : 'itens'}{filteredAssets.length !== assets.length && <span className="text-gray-500"> de {assets.length} total</span>}</div>
                        </div>
                        {loading ? (
                            <div className="text-center py-12"><Loader2 className="animate-spin h-16 w-16 text-primary mx-auto" /><p className="mt-4 text-lg">{appliedSearchTerm ? `Buscando "${appliedSearchTerm}"...` : 'Carregando...'}</p></div>
                        ) : filteredAssets.length === 0 ? (
                            <div className="text-center py-12"><Video className="w-16 h-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-500 text-lg">Nenhuma mídia encontrada</p><button onClick={clearAllFilters} className="mt-4 bg-primary hover:bg-primary-hover transition-colors text-white px-4 py-2 rounded-lg">Limpar filtros e recarregar</button></div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredAssets.map((asset) => (
                                    <div key={asset.public_id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
                                        <AssetThumbnail asset={asset} onClick={() => setSelectedAsset(asset)} />
                                        <div className="p-4 flex flex-col flex-1">
                                            {asset.metadata?.montadora && <div className="flex flex-wrap gap-1 mb-3"><span className="text-gray-400 text-sm flex items-center gap-1 w-fit cursor-pointer hover:text-gray-600" onClick={(e) => { e.stopPropagation(); if (asset.metadata?.montadora) { setSelectedMontadora(asset.metadata.montadora); setAppliedSelectedMontadora(asset.metadata.montadora); } }}>{asset.metadata.montadora.toUpperCase()}</span></div>}
                                            <h3 className="font-bold text-lg mb-2 truncate">{asset.context?.custom?.title || asset.display_name}</h3>
                                            {(asset.context?.alt || asset.metadata?.legenda) && <p className="text-gray-600 text-sm mb-3 line-clamp-2">{asset.context?.alt || asset.metadata?.legenda}</p>}
                                            {asset.tags && asset.tags.length > 0 && <div className="flex flex-wrap gap-1 mb-3">{asset.tags.slice(0, 3).map((tag: string, index: number) => <span key={index} className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full flex items-center gap-1 cursor-pointer hover:bg-yellow-200 transition-colors" onClick={(e) => { e.stopPropagation(); setSelectedTag(tag); setAppliedSelectedTag(tag); }}><Hash className="w-2 h-2" />{tag}</span>)}{asset.tags.length > 3 && <span className="text-gray-500 text-xs py-1">+{asset.tags.length - 3} mais</span>}</div>}
                                            <div className="text-xs text-gray-500 mb-3 flex items-center gap-3 grow mt-auto">
                                                {asset.format && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(asset.created_at).toLocaleDateString('pt-BR')}</span>}
                                                {asset.resource_type === 'video' && asset.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{Math.floor(asset.duration / 60).toString().padStart(2, '0')}:{Math.floor(asset.duration % 60).toString().padStart(2, '0')}</span>}
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleDownload(asset)} disabled={downloadingAssets.has(asset.public_id)} className="hidden md:flex flex-1 bg-gray-300 text-black px-3 py-2 rounded text-sm hover:bg-gray-400 items-center justify-center gap-2 disabled:opacity-50 transition-colors">{downloadingAssets.has(asset.public_id) ? <><Loader2 className="w-4 h-4 animate-spin" />Baixando...</> : <><Download className="w-4 h-4" />Baixar</>}</button>
                                                <button onClick={(e) => { e.stopPropagation(); shareAssetViaWebShare(asset); }} className="flex-1 bg-whatsapp hover:bg-whatsapp-hover text-white px-3 py-2 rounded text-sm flex items-center justify-center gap-2 transition-colors"><MessageCircle className="w-4 h-4" />Compartilhar</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <UserManagement />
            )}
        </div>
      </main>
      
      {selectedAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg md:text-xl font-bold pr-4 truncate">{selectedAsset.context?.custom?.title || selectedAsset.display_name}</h2>
                <button onClick={() => setSelectedAsset(null)} className="text-gray-500 hover:text-gray-700 p-2 -m-2"><X className="w-6 h-6" /></button>
              </div>
              <div className="mb-4 bg-gray-100 rounded flex justify-center items-center">
                {selectedAsset.resource_type === 'video' ? (
                  <video className="w-full max-h-60 md:max-h-96 rounded" controls autoPlay>
                    <source src={selectedAsset.secure_url} type={`video/${selectedAsset.format}`} />
                    Seu navegador não suporta o elemento de vídeo.
                  </video>
                ) : (
                  <img src={selectedAsset.secure_url} alt={selectedAsset.context?.alt || selectedAsset.display_name} className="max-w-full max-h-60 md:max-h-96 rounded" />
                )}
              </div>
              <div className="space-y-3">
                {selectedAsset.metadata?.montadora && <div className="flex flex-wrap gap-1 mb-3"><span className="text-gray-400 text-sm flex items-center gap-1 w-fit cursor-pointer hover:text-gray-600" onClick={() => { if (selectedAsset.metadata?.montadora) { setSelectedMontadora(selectedAsset.metadata.montadora); setAppliedSelectedMontadora(selectedAsset.metadata.montadora); setSelectedAsset(null); } }}>{selectedAsset.metadata.montadora.toUpperCase()}</span></div>}
                {(selectedAsset.context?.alt || selectedAsset.metadata?.legenda) && <div><p className="text-gray-600 text-sm md:text-base">{selectedAsset.context?.alt || selectedAsset.metadata?.legenda}</p></div>}
                {selectedAsset.tags && selectedAsset.tags.length > 0 && <div><div className="flex flex-wrap gap-2">{selectedAsset.tags.map((tag: string, index: number) => <span key={index} className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm flex items-center gap-1 cursor-pointer hover:bg-yellow-200 transition-colors" onClick={() => { setSelectedTag(tag); setAppliedSelectedTag(tag); setSelectedAsset(null); }}><Hash className="w-3 h-3" />{tag}</span>)}</div></div>}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-4 border-t">
                  <div className="text-sm text-gray-500 flex items-center gap-1"><Calendar className="w-4 h-4" />Adicionado em {new Date(selectedAsset.created_at).toLocaleDateString('pt-BR')}</div>
                  <div className="flex gap-2">
                    <button onClick={() => handleDownload(selectedAsset)} disabled={downloadingAssets.has(selectedAsset.public_id)} className="hidden md:flex bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400 items-center justify-center gap-2 disabled:opacity-50 min-h-[44px] transition-colors">{downloadingAssets.has(selectedAsset.public_id) ? <><Loader2 className="w-4 h-4 animate-spin" />Baixando...</> : <><Download className="w-4 h-4" />Baixar</>}</button>
                    <button onClick={() => { shareAssetViaWebShare(selectedAsset); }} className="bg-whatsapp hover:bg-whatsapp-hover text-white px-4 py-2 rounded flex items-center justify-center gap-2 min-h-[44px] transition-colors"><MessageCircle className="w-4 h-4" />Compartilhar</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {shareNotification && (
        <div className="fixed top-10 left-1/2 -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl z-[9999] flex items-center gap-3">
          <CheckCircle className="w-5 h-5" />
          <p className="font-semibold">{shareNotification}</p>
        </div>
      )}

      <footer className="text-gray-500 text-xs px-6 pb-6 mt-auto text-center">
        <div className="max-w-6xl mx-auto">
          <p>Logado como: <span className="font-semibold text-gray-500">{user?.email}</span></p>
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  if (window.location.pathname === '/confirm') {
    return <ActionConfirmationView />;
  }
  
  return <MainApp />;
};

export default App;


